import { useQuery } from '@tanstack/react-query';
import { AxiosError, HttpStatusCode } from 'axios';
import { lazy, Suspense, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { getGlobalConfigByType } from 'src/api/globalConfigApi';
import { getOrganizationDetailBySlug } from 'src/api/organizationApi';
import SuspenseProgressBridge from 'src/components/SuspenseProgressBridge';
import GuardError from 'src/guards/components/guardError';
import { UserLayout } from 'src/layouts';
import { globalConfigAction, orgAction } from 'src/redux/actions';
import { GlobalConfigType } from 'src/utils/proptypes';

const NotFoundPage = lazy(() => import('src/pages/notFound'));

const OrganizationGuard = ({ children }: any) => {
   const dispatch = useDispatch();
   const params = useParams();

   const { data: globalConfig } = useQuery({
      queryKey: ['getGlobalConfigs'],
      queryFn: () => getGlobalConfigByType(GlobalConfigType.GLOBAL),
      retry: false,
   });

   useEffect(() => {
      if (globalConfig?.data) {
         if (globalConfig?.data?.configure?.landingVideo?.s3Path) {
            dispatch(globalConfigAction.setGlobalVideo(globalConfig?.data?.configure?.landingVideo?.s3Path));
         }
      }
   }, [globalConfig]);

   const { data, error, isError, refetch } = useQuery({
      queryKey: ['getOrganizationDetailBySlug', params.organizationSlug],
      queryFn: () => (params.organizationSlug ? getOrganizationDetailBySlug(params.organizationSlug) : null),
      retry: false,
      // Was staleTime: 0 / refetchOnMount: true, which refetched immediately and
      // discarded the value pages/c/@organizationSlug/+Page.tsx seeds from the
      // SSR payload — the whole point of seeding is to skip that round trip.
      // A minute of in-session staleness is invisible to travelers (they hard
      // load, get an SSR-fresh org, and leave); admin saves invalidate this key
      // explicitly (src/pages/admin/organization/AddEdit.tsx).
      staleTime: 60_000,
      gcTime: 5 * 60_000,
   });

   useEffect(() => {
      if (data?.data) {
         if (data?.data?.isActive) {
            dispatch(orgAction.setOrganization(data?.data));
         }
      }
   }, [data?.data]);

   if (isError && error) {
      const axiosError = error as AxiosError;
      if (axiosError?.status === HttpStatusCode.NotFound) {
         return (
            <UserLayout>
               <Suspense fallback={<SuspenseProgressBridge />}>
                  <NotFoundPage />
               </Suspense>
            </UserLayout>
         );
      }

      return (
         <UserLayout>
            <GuardError onRetry={() => refetch()} />
         </UserLayout>
      );
   }

   if (data?.data?.isActive === false) {
      return (
         <UserLayout>
            <Suspense fallback={<SuspenseProgressBridge />}>
               <NotFoundPage />
            </Suspense>
         </UserLayout>
      );
   }

   return children;
};

export default OrganizationGuard;
